import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, Input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { IonNav, ModalController, ViewWillEnter, ViewWillLeave } from '@ionic/angular';
import { faArrowLeft, faArrowRight, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { TranslocoPipe } from '@jsverse/transloco';
import { FullPageLayoutComponent } from '@app/core/layout/full-page-layout/full-page-layout.component';
import { PdfViewComponent } from '@app/shared/ui/pdf-view/pdf-view.component';
import { ButtonComponent } from '@app/shared/ui/button/button.component';
import { SharedModule } from '@app/shared/shared.module';
import { SignDocumentBatchSandbox } from '@app/document-bundle-signing/sandboxes/sign-document-batch.sandbox';
import { DocumentSkeletonComponent } from '@app/documents/components/document-skeleton/document-skeleton.component';
import { SignDocumentBatchOverviewPageComponent } from '../overview-page/overview-page.component';

@Component({
  selector: 'c-sign-document-page',
  imports: [
    FullPageLayoutComponent,
    PdfViewComponent,
    ButtonComponent,
    DocumentSkeletonComponent,
    SharedModule,
    ReactiveFormsModule,
    TranslocoPipe,
  ],
  templateUrl: './sign-document-page.component.html',
  styleUrl: './sign-document-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SignDocumentPageComponent implements OnInit, ViewWillEnter, ViewWillLeave {
  @Input() index = 0;
  // When opened from the overview the page is a single-document review: the only
  // forward action is returning to the overview, not walking to the next document.
  @Input() reviewMode = false;

  private readonly sandbox = inject(SignDocumentBatchSandbox);
  private readonly nav = inject(IonNav);
  private readonly modalCtrl = inject(ModalController);
  private readonly destroyRef = inject(DestroyRef);

  readonly backIcon = faArrowLeft;
  readonly closeIcon = faXmark;
  readonly nextIcon = faArrowRight;

  readonly agreeControl = new FormControl<boolean>(false, { nonNullable: true });

  readonly document = computed(() => this.sandbox.documents()[this.index]);
  readonly documentId = computed(() => this.document()?.documentId);
  readonly total = computed(() => this.sandbox.documents().length);
  readonly title = computed(() => {
    const document = this.document();
    return document ? `${document.name} (${this.index + 1}/${this.total()})` : '';
  });
  readonly isFirst = computed(() => this.index === 0);
  readonly isLast = computed(() => this.index >= this.total() - 1);
  readonly preview = computed(() => this.sandbox.previewFor(this.documentId()));

  // Only the visible page keeps its <c-pdf-view> mounted: ion-nav keeps prior
  // pages alive, and multiple simultaneous ngx-extended-pdf-viewer instances
  // conflict (later ones render blank).
  readonly active = signal(true);

  constructor() {
    effect(() => this.syncAgreeControl());
  }

  ngOnInit(): void {
    this.syncAgreeControl();

    this.agreeControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        const documentId = this.documentId();
        if (documentId) {
          this.sandbox.setAgreed(documentId, value);
        }
      });
  }

  ionViewWillEnter(): void {
    this.active.set(true);
    this.sandbox.downloadDocument(this.documentId());
  }

  ionViewWillLeave(): void {
    this.active.set(false);
  }

  next(): void {
    if (this.isLast()) {
      this.nav.push(SignDocumentBatchOverviewPageComponent, {
        onOpenDocument: (index: number) => this.nav.push(SignDocumentPageComponent, { index, reviewMode: true }),
      });
      return;
    }
    this.nav.push(SignDocumentPageComponent, { index: this.index + 1 });
  }

  back(): void {
    this.nav.pop();
  }

  close(): void {
    this.modalCtrl.dismiss();
  }

  private syncAgreeControl(): void {
    const isAgreed = this.sandbox.isAgreed(this.documentId());
    if (this.agreeControl.value !== isAgreed) {
      this.agreeControl.setValue(isAgreed, { emitEvent: false });
    }
  }
}
